import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { easeExpo } from "@/components/Reveal";

const links = [
  { to: "/", label: "Home", testid: "nav-link-home" },
  { to: "/services", label: "Services", testid: "nav-link-services" },
  { to: "/about", label: "About", testid: "nav-link-about" },
  { to: "/contact", label: "Contact", testid: "nav-link-contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  return (
    <header
      className="fixed inset-x-0 top-0 z-50 bg-paper/85 backdrop-blur-xl backdrop-saturate-150 border-b border-ink/10"
      data-testid="site-header"
    >
      <div className="mx-auto max-w-[1600px] px-6 lg:px-12 h-20 flex items-center justify-between">
        <Link
          to="/"
          className="flex items-center gap-3 group"
          data-testid="site-logo"
          onClick={() => setOpen(false)}
        >
          <img
            src="/logo-mark.png"
            alt="KC Consultancy"
            className="h-10 w-auto object-contain"
          />
          <span className="hidden sm:block leading-none">
            <span className="block font-display text-lg font-semibold tracking-tight text-ink">KC Consultancy</span>
            <span className="block font-mono text-[9px] sm:text-[10px] tracking-[0.22em] uppercase text-slateblue mt-1">
              Specialist Youth Justice Consultancy
            </span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-10" data-testid="desktop-nav">
          {links.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              data-testid={link.testid}
              className={({ isActive }) =>
                `link-underline text-sm tracking-wide transition-colors duration-300 ${
                  isActive ? "is-active text-ink" : "text-ink/60 hover:text-ink"
                }`
              }
            >
              {link.label}
            </NavLink>
          ))}
          <Link
            to="/contact"
            data-testid="nav-cta-button"
            className="inline-flex items-center gap-2 rounded-full bg-ink px-6 py-2.5 text-sm text-paper transition-colors duration-300 hover:bg-brand"
          >
            Contact Us
            <ArrowUpRight className="h-4 w-4" />
          </Link>
        </nav>

        <button
          className="md:hidden text-ink p-2"
          onClick={() => setOpen((v) => !v)}
          data-testid="mobile-menu-button"
          aria-label="Toggle menu"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            key="mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.4, ease: easeExpo }}
            className="md:hidden overflow-hidden border-t border-ink/10 bg-paper"
            data-testid="mobile-menu-panel"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {links.map((link) => (
                <NavLink
                  key={link.to}
                  to={link.to}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-${link.testid}`}
                  className={({ isActive }) =>
                    `font-display text-2xl ${isActive ? "text-brand" : "text-ink"}`
                  }
                >
                  {link.label}
                </NavLink>
              ))}
              <Link
                to="/contact"
                onClick={() => setOpen(false)}
                data-testid="mobile-nav-cta-button"
                className="mt-2 inline-flex w-fit items-center gap-2 rounded-full bg-ink px-6 py-3 text-sm text-paper"
              >
                Contact Us
                <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
